import { Toggle, useBreakpoint } from "@povio/ui";
import { cx } from "class-variance-authority";
import Image from "next/image";
import { type FC, type SVGProps, useEffect, useRef, useState } from "react";

import { CheckIcon } from "@/assets/icons/general/Check";
import { CloseIcon } from "@/assets/icons/general/Close";
import { InfoIcon } from "@/assets/icons/general/Info";
import { PenAndBookIcon } from "@/assets/icons/general/PenAndBook";
import { StopIcon } from "@/assets/icons/general/Stop";
import { TrashIcon } from "@/assets/icons/general/Trash";
import { CircleIcon } from "@/assets/icons/ui/Circle";
import ProfileShowMoreButton from "@/components/home/sections/components/ProfileShowMoreButton";
import { EndorsementTab } from "@/components/home/sections/ProfileEndorsementsSection";
import { Button } from "@/components/ui/buttons/Button/Button";
import { InlineIconButton } from "@/components/ui/buttons/InlineIconButton/InlineIconButton";
import { Modal } from "@/components/ui/overlays/Modal/Modal";
import { Typography } from "@/components/ui/text/Typography/Typography";
import { AuthContext } from "@/data/auth/auth.context";
import { CommonModels } from "@/data/common/common.models";
import { DemoContext } from "@/providers/demo/DemoContext";

interface IProps {
  endorsement: CommonModels.EndorsementDto;
  activeTab?: EndorsementTab;
  hideActions?: boolean;
  onAcceptEndorsement?: (endorsement: CommonModels.EndorsementDto) => void;
  onToggleEndorsementVisibility?: (endorsement: CommonModels.EndorsementDto) => void;
  onDeleteEndorsement?: (endorsement: CommonModels.EndorsementDto) => void;
  onWriteEndorsement?: (endorsement: CommonModels.EndorsementDto) => void;
  onCancelEndorsement?: (endorsement: CommonModels.EndorsementDto) => void;
  onCancelEndorsementRequest?: (endorsement: CommonModels.EndorsementDto) => void;
  onUnsendEndorsement?: (endorsement: CommonModels.EndorsementDto) => void;
  onIgnoreEndorsement?: (endorsement: CommonModels.EndorsementDto) => void;
}

const EndorsementsItem = ({
  endorsement,
  activeTab,
  hideActions,
  onAcceptEndorsement,
  onToggleEndorsementVisibility,
  onDeleteEndorsement,
  onWriteEndorsement,
  onCancelEndorsement,
  onCancelEndorsementRequest,
  onUnsendEndorsement,
  onIgnoreEndorsement,
}: IProps) => {
  const { user: authUser } = AuthContext.useAuth();
  const [isExpanded, setIsExpanded] = useState(false);
  const [showButton, setShowButton] = useState(false);
  const [isCancelApproveEndorsementModalOpen, setIsCancelApproveEndorsementModalOpen] = useState(false);
  const textRef = useRef<HTMLDivElement>(null);
  const isDesktop = useBreakpoint("md");
  const { isDemoMode } = DemoContext.useDemo();
  const isUserRequestingEndorsement = endorsement.status === CommonModels.EndorsementStatusEnum.REQUESTED;

  const isCurrentUserOwner = endorsement.userId === authUser?.userId;
  const userProfileImageUrl =
    isCurrentUserOwner || hideActions ? endorsement.endorserProfilePictureUrl : endorsement.userProfilePictureUrl || "";

  const endorserNameOrEmail = endorsement.endorserName || endorsement.endorserEmail;
  const userNameOrEmail = endorsement.userName || endorsement.userEmail;

  const name = isCurrentUserOwner || hideActions ? endorserNameOrEmail : userNameOrEmail || "";

  const content = endorsement.content || "No description available";

  const canAcceptEndorsement =
    endorsement.status === CommonModels.EndorsementStatusEnum.PENDING_APPROVAL && isCurrentUserOwner && !hideActions;
  const canUnsendEndorsement =
    endorsement.status === CommonModels.EndorsementStatusEnum.PENDING_APPROVAL && !isCurrentUserOwner && !hideActions;

  const canEndorseOrIgnoreEndorsement =
    endorsement.status === CommonModels.EndorsementStatusEnum.REQUESTED && !hideActions;

  const canCancelApproveEndorsement =
    endorsement.userId !== authUser?.userId &&
    endorsement.status === CommonModels.EndorsementStatusEnum.APPROVED &&
    !hideActions;
  const canDeleteEndorsement =
    endorsement.userId === authUser?.userId &&
    endorsement.status === CommonModels.EndorsementStatusEnum.APPROVED &&
    !hideActions;
  const requiresUsersAction =
    (canAcceptEndorsement && isCurrentUserOwner) || (canEndorseOrIgnoreEndorsement && !isCurrentUserOwner);
  const canHideOrShowEndorsement =
    activeTab === EndorsementTab.INCOMING && !hideActions && !canAcceptEndorsement && !isUserRequestingEndorsement;

  useEffect(() => {
    if (textRef.current) {
      const lineHeight = parseInt(window.getComputedStyle(textRef.current).lineHeight, 10);
      const height = textRef.current.scrollHeight;
      const numberOfLines = Math.round(height / lineHeight);
      setShowButton(numberOfLines > 3);
    }
  }, [content]);

  if (isDesktop) {
    return (
      <div className="endorsement-item flex h-full flex-row flex-wrap dd:flex-nowrap items-start p-3">
        <Image
          src={userProfileImageUrl || "/default-avatar.png"}
          width={150}
          height={150}
          alt="Profile"
          className="max-h-[150px] min-h-[150px] min-w-[150px] max-w-[150px] rounded-sm object-cover"
        />
        <div className="flex w-9 items-center justify-center pt-1-5">
          {requiresUsersAction && <CircleIcon className="h-3 w-3 text-blue-200" />}
        </div>
        <div className="flex flex-1 flex-col gap-1">
          <div className="flex flex-col items-start gap-1">
            <Typography
              size="h4"
              className="font-weight-600"
            >
              {name}
            </Typography>
            <div className="flex flex-row items-center gap-4">
              {endorsement.relationship && (
                <>
                  <Typography
                    size="labels-input-m"
                    className="text-neutral-200"
                  >
                    {endorsement.relationship}
                  </Typography>
                  <div className="h-2-5 w-0-5 bg-neutral-400" />
                </>
              )}
              <Typography
                size="body-paragraph-m"
                className="text-neutral-50"
              >
                {new Date(endorsement.createdAt).toLocaleDateString("en-US", {
                  year: "numeric",
                  month: "long",
                  day: "numeric",
                })}
              </Typography>
            </div>
          </div>
          <div className="endorsement-content-wrapper">
            {!isUserRequestingEndorsement && (
              <Typography
                ref={textRef}
                size="body-paragraph-m"
                className={cx(
                  `endorsement-description text-neutral-50 transition-all duration-300`,
                  !isExpanded ? "line-clamp-3 max-h-[72px] overflow-hidden" : "max-h-[1000px] whitespace-pre-line",
                )}
                id="description"
              >
                {content}
              </Typography>
            )}
            {isUserRequestingEndorsement && (
              <Typography
                size="body-paragraph-m"
                className="font-primary text-body-paragraph-m text-neutral-50 italic"
              >
                {endorsement.userId === authUser?.userId &&
                  `You’ve requested an endorsement from ${endorserNameOrEmail}`}
                {endorsement.userId !== authUser?.userId &&
                  `${userNameOrEmail} has sent you a request for an endorsement`}
              </Typography>
            )}
            {showButton && (
              <ProfileShowMoreButton
                isExpanded={isExpanded}
                setIsExpanded={setIsExpanded}
              />
            )}
          </div>
        </div>
        <div className="endorsement-actions ml-10 flex min-h-[150px] max-w-[180px] flex-auto flex-col items-end justify-start gap-2-5">
          {canHideOrShowEndorsement && (
            <Toggle
              isDisabled={isDemoMode}
              className="min-w-36 flex-row-reverse"
              onChange={() => onToggleEndorsementVisibility?.(endorsement)}
              defaultSelected
              isSelected={endorsement.visible}
              // labelClassName="!text-body-paragraph-m !font-weight-400 !font-primary !text-neutral-100"
            >
              {endorsement.visible ? "Hide" : "Make Visible"}
            </Toggle>
          )}

          {canDeleteEndorsement && (
            <ActionButton
              icon={TrashIcon}
              label="Delete"
              isDisabled={isDemoMode}
              onPress={() => onDeleteEndorsement?.(endorsement)}
            />
          )}
          {canAcceptEndorsement && (
            <ActionButton
              icon={CheckIcon}
              isDisabled={isDemoMode}
              label="Accept Endorsement"
              onPress={() => onAcceptEndorsement?.(endorsement)}
            />
          )}
          {canAcceptEndorsement && (
            <ActionButton
              icon={StopIcon}
              isDisabled={isDemoMode}
              label="Ignore Endorsement"
              onPress={() => onIgnoreEndorsement?.(endorsement)}
            />
          )}
          {canEndorseOrIgnoreEndorsement && !isCurrentUserOwner && (
            <ActionButton
              icon={PenAndBookIcon}
              isDisabled={isDemoMode}
              label="Write Endorsement"
              onPress={() => onWriteEndorsement?.(endorsement)}
            />
          )}
          {canEndorseOrIgnoreEndorsement && (
            <ActionButton
              icon={StopIcon}
              isDisabled={isDemoMode}
              label={isCurrentUserOwner ? "Cancel Request" : "Cancel Endorsement"}
              onPress={() => {
                if (isCurrentUserOwner) {
                  onCancelEndorsementRequest?.(endorsement);
                } else {
                  onCancelEndorsement?.(endorsement);
                }
              }}
            />
          )}

          {(canCancelApproveEndorsement || canUnsendEndorsement) && (
            <ActionButton
              icon={StopIcon}
              isDisabled={isDemoMode}
              label="Cancel Endorsement"
              onPress={() => {
                setIsCancelApproveEndorsementModalOpen(true);
              }}
            />
          )}

          <Modal
            isOpen={isCancelApproveEndorsementModalOpen}
            onClose={() => {
              setIsCancelApproveEndorsementModalOpen(false);
            }}
            hideCloseButton
            dialogClassName="t:!pt-[30px] t:!pb-[36px]"
            aside="bounce"
          >
            <div className="flex min-w-[500px] max-w-[500px] flex-1 flex-col overflow-y-auto">
              <div className="relative mb-5 h-[15px]">
                <InlineIconButton
                  onPress={() => {
                    setIsCancelApproveEndorsementModalOpen(false);
                  }}
                  icon={CloseIcon}
                  className="absolute t:top-0 right-0 z-10 p-0"
                  iconClassName="h-[15px] w-[15px] text-white"
                  label="close"
                />
              </div>
              <Typography
                size="h5"
                className="mb-6 flex flex-row gap-2 font-weight-600 text-primary-200"
              >
                <InfoIcon className="h-5 w-5 text-primary-200" />

                {isCancelApproveEndorsementModalOpen && "Cancel pending endorsement"}
              </Typography>
              <Typography
                size="body-paragraph-m"
                className="mb-10"
              >
                Are you certain you want to cancel this endorsement? This will permanently be removed from your records.
              </Typography>
              <div className="flex justify-end gap-3">
                <Button
                  variant="outlined"
                  className="h-[38px] py-2!"
                  labelClassName="!text-labels-m !font-weight-600"
                  onPress={() => {
                    onUnsendEndorsement?.(endorsement);
                    setIsCancelApproveEndorsementModalOpen(false);
                  }}
                >
                  Yes, cancel
                </Button>
                <Button
                  variant="primary"
                  type="submit"
                  data-testid="introduction-save-button"
                  className="h-[38px] py-2!"
                  labelClassName="!text-labels-m !font-weight-500"
                  onPress={() => {
                    setIsCancelApproveEndorsementModalOpen(false);
                  }}
                >
                  No, don&apos;t cancel
                </Button>
              </div>
            </div>
          </Modal>
        </div>
      </div>
    );
  }
  return (
    <div className="endorsement-item flex h-full flex-col items-start p-3">
      <div className="flex flex-row items-start">
        <Image
          src={userProfileImageUrl || "/default-avatar.png"}
          width={150}
          height={150}
          alt="Profile"
          className="max-h-[150px] min-h-[150px] min-w-[150px] max-w-[150px] rounded-sm object-cover"
        />
        <div className="flex w-5 items-center justify-center pt-1-5">
          {requiresUsersAction && <CircleIcon className="h-3 w-3 text-blue-200" />}
        </div>
        <div className="flex w-full flex-col items-start gap-1">
          <Typography
            size="h4"
            className="break-all font-weight-600"
          >
            {name}
          </Typography>
          <div className="flex flex-row items-center gap-4">
            {endorsement.relationship && (
              <>
                <Typography
                  size="labels-input-m"
                  className="text-neutral-200"
                >
                  {endorsement.relationship}
                </Typography>
                <div className="h-2-5 w-0-5 bg-neutral-400" />
              </>
            )}
            <Typography
              size="body-paragraph-m"
              className="text-neutral-50"
            >
              {new Date(endorsement.createdAt).toLocaleDateString("en-US", {
                year: "numeric",
                month: "long",
                day: "numeric",
              })}
            </Typography>
          </div>
        </div>
      </div>

      <div className="endorsement-content-wrapper">
        {!isUserRequestingEndorsement && (
          <Typography
            ref={textRef}
            size="body-paragraph-m"
            className={cx(
              `endorsement-description text-neutral-50 transition-all duration-300`,
              !isExpanded ? "line-clamp-3 max-h-[72px] overflow-hidden" : "max-h-[1000px] whitespace-pre-line",
            )}
            id="description"
          >
            {content}
          </Typography>
        )}
        {isUserRequestingEndorsement && (
          <Typography
            size="body-paragraph-m"
            className="font-primary text-neutral-50 italic"
          >
            {endorsement.userId === authUser?.userId && `You’ve requested an endorsement from ${endorserNameOrEmail}`}
            {endorsement.userId !== authUser?.userId && `${userNameOrEmail} has sent you a request for an endorsement`}
          </Typography>
        )}
        {showButton && (
          <ProfileShowMoreButton
            isExpanded={isExpanded}
            setIsExpanded={setIsExpanded}
          />
        )}
      </div>

      <div className="mt-3 ml-10 flex max-w-[180px] flex-auto flex-row flex-wrap items-center justify-start gap-3">
        {canHideOrShowEndorsement && (
          <Toggle
            className="flex-row-reverse"
            onChange={() => onToggleEndorsementVisibility?.(endorsement)}
            defaultSelected
            isSelected={endorsement.visible}
            // labelClassName="!text-body-paragraph-m !font-weight-400 !font-primary !text-neutral-100"
          >
            {endorsement.visible ? "Hide" : "Make Visible"}
          </Toggle>
        )}

        {canDeleteEndorsement && (
          <ActionButton
            icon={TrashIcon}
            label="Delete"
            onPress={() => onDeleteEndorsement?.(endorsement)}
          />
        )}
        {canAcceptEndorsement && (
          <ActionButton
            icon={CheckIcon}
            label="Accept Endorsement"
            onPress={() => onAcceptEndorsement?.(endorsement)}
          />
        )}
        {canAcceptEndorsement && (
          <ActionButton
            icon={StopIcon}
            label="Ignore Endorsement"
            onPress={() => onIgnoreEndorsement?.(endorsement)}
          />
        )}
        {canEndorseOrIgnoreEndorsement && !isCurrentUserOwner && (
          <ActionButton
            icon={PenAndBookIcon}
            label="Write Endorsement"
            onPress={() => onWriteEndorsement?.(endorsement)}
          />
        )}
        {canEndorseOrIgnoreEndorsement && (
          <ActionButton
            icon={StopIcon}
            label={isCurrentUserOwner ? "Cancel Request" : "Cancel Endorsement"}
            onPress={() => {
              if (isCurrentUserOwner) {
                onCancelEndorsementRequest?.(endorsement);
              } else {
                onCancelEndorsement?.(endorsement);
              }
            }}
          />
        )}

        {(canCancelApproveEndorsement || canUnsendEndorsement) && (
          <ActionButton
            icon={StopIcon}
            label="Cancel Endorsement"
            onPress={() => {
              setIsCancelApproveEndorsementModalOpen(true);
            }}
          />
        )}

        <Modal
          isOpen={isCancelApproveEndorsementModalOpen}
          onClose={() => {
            setIsCancelApproveEndorsementModalOpen(false);
          }}
          hideCloseButton
          dialogClassName="t:!pt-[30px] t:!pb-[36px]"
          aside="bounce"
        >
          <div className="flex min-w-[500px] max-w-[500px] flex-1 flex-col overflow-y-auto">
            <div className="relative mb-5 h-[15px]">
              <InlineIconButton
                onPress={() => {
                  setIsCancelApproveEndorsementModalOpen(false);
                }}
                icon={CloseIcon}
                className="absolute t:top-0 right-0 z-10 p-0"
                iconClassName="h-[15px] w-[15px] text-white"
                label="close"
              />
            </div>
            <Typography
              size="h5"
              className="mb-6 flex flex-row gap-2 font-weight-600 text-primary-200"
            >
              <InfoIcon className="h-5 w-5 text-primary-200" />
              {isCancelApproveEndorsementModalOpen && "Cancel pending endorsement"}
            </Typography>
            <Typography
              size="body-paragraph-m"
              className="mb-10"
            >
              Are you certain you want to cancel this endorsement? This will permanently be removed from your records.
            </Typography>
            <div className="flex justify-end gap-3">
              <Button
                variant="outlined"
                className="h-[38px] py-2!"
                labelClassName="!font-weight-400"
                onPress={() => {
                  onUnsendEndorsement?.(endorsement);
                  setIsCancelApproveEndorsementModalOpen(false);
                }}
              >
                Yes, cancel
              </Button>
              <Button
                variant="primary"
                type="submit"
                data-testid="introduction-save-button"
                className="h-[38px] py-2!"
                labelClassName="!font-weight-400"
                onPress={() => {
                  setIsCancelApproveEndorsementModalOpen(false);
                }}
              >
                No, don&apos;t cancel
              </Button>
            </div>
          </div>
        </Modal>
      </div>
    </div>
  );
};

const ActionButton = ({
  isDisabled,
  icon,
  label,
  onPress,
}: {
  isDisabled?: boolean;
  icon: FC<SVGProps<SVGSVGElement>> | undefined;
  label: string;
  onPress: () => void;
}) => {
  return (
    <Button
      isDisabled={isDisabled}
      icon={icon}
      variant="text"
      width="auto"
      className="px-0! py-0!"
      labelClassName="!text-body-paragraph-m !font-weight-400 !font-primary !text-neutral-100 !underline"
      iconClassName="!h-4 !w-4 ml-1 !text-neutral-100"
      iconPosition="right"
      onPress={onPress}
    >
      {label}
    </Button>
  );
};

export default EndorsementsItem;
